import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import type { CoverDoc } from 'fym-shared/src/music24/coverDoc';
import { useCreateCoverMakerLeadMutation } from '../../../redux/api/fymAPI';
import { hasTrackingConsent } from '../../analytics/consent/trackingConsent';
import { exportCoverJpeg } from '../render/exportCoverJpeg';
import { coverExportFileName } from '../render/exportRules';
import { saveBlob } from '../topbar/useDownloadCover';
import { coverLeadEmail, coverLeadError, coverLeadRequest, needsLeadEmail, type CoverLeadError } from './coverLead';
import { makeLeadThumbnail } from './leadThumbnail';
import {
  browserCoverStorage,
  readLeadEmail,
  readLeadToken,
  readPendingEmail,
  writeLeadEmail,
  writeLeadToken,
  writePendingEmail,
} from './publicCoverStore';

export type CoverLeadDialog = 'closed' | 'email' | 'done';

interface UseCoverLeadDownloadProps {
  doc: CoverDoc;
  title: string;
  artist: string;
  /** A signed-in visitor (the embed on the website): no email dialog. */
  isSignedIn: boolean;
}

/**
 * Download of the public tool (owner decisions D7, D12; design g3, g4). The first download of a
 * browser asks for the email: the lead (the email, the design, a small picture) goes to the API,
 * and then the browser draws and saves the 3000 px JPEG with the watermark. A later download of the
 * same browser starts at once and updates the saved design.
 *
 * A refusal of the API (a bad or temporary address, too many downloads) keeps the dialog open and
 * the file does not start. When the API gives no answer or a 5xx, the file starts anyway and the
 * email waits in this browser for the next download: a visitor never loses the result because our
 * server is down.
 */
export function useCoverLeadDownload({ doc, title, artist, isSignedIn }: UseCoverLeadDownloadProps) {
  const intl = useIntl();
  const [createLead] = useCreateCoverMakerLeadMutation();
  const [dialog, setDialog] = useState<CoverLeadDialog>('closed');
  const [error, setError] = useState<Exclude<CoverLeadError, 'down'> | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [isFileFailed, setIsFileFailed] = useState(false);
  /** The address that got the link; null when the lead waits for the API. */
  const [sentTo, setSentTo] = useState<string | null>(null);

  const saveFile = useCallback(async (): Promise<boolean> => {
    setIsFileFailed(false);
    try {
      const { blob, side } = await exportCoverJpeg(doc, { watermark: true });
      saveBlob(blob, coverExportFileName({ artist, title, side }));
      return true;
    } catch (caught) {
      console.error('Cover maker: the download failed', caught);
      setIsFileFailed(true);
      return false;
    }
  }, [artist, doc, title]);

  const sendLead = useCallback(
    async (email: string): Promise<void> => {
      const thumbnail = await makeLeadThumbnail(doc);
      const storage = browserCoverStorage();
      const request = coverLeadRequest({
        email,
        doc,
        title,
        artist,
        thumbnail,
        locale: intl.locale,
        trackingConsent: hasTrackingConsent(),
        leadToken: readLeadToken(storage),
      });
      const { token } = await createLead(request).unwrap();
      writeLeadToken(storage, token);
      writeLeadEmail(storage, email);
      writePendingEmail(storage, null);
    },
    [artist, createLead, doc, intl.locale, title]
  );

  const start = useCallback(() => {
    const storage = browserCoverStorage();
    const pendingEmail = readPendingEmail(storage);
    if (needsLeadEmail({ isSignedIn, leadToken: readLeadToken(storage), pendingEmail })) {
      setError(null);
      setDialog('email');
      return;
    }
    setIsBusy(true);
    // A known visitor: the file starts at once; the saved design is updated beside it.
    const email = pendingEmail ?? readLeadEmail(storage);
    if (!isSignedIn && email !== null) {
      sendLead(email).catch((caught: unknown) =>
        console.error('Cover maker: the saved design was not updated', caught)
      );
    }
    saveFile()
      .catch(console.error)
      .finally(() => setIsBusy(false));
  }, [isSignedIn, saveFile, sendLead]);

  const submit = useCallback(
    async (value: string): Promise<void> => {
      const email = coverLeadEmail(value);
      if (email === null) {
        setError('invalid');
        return;
      }
      setError(null);
      setIsBusy(true);
      try {
        await sendLead(email);
        setSentTo(email);
      } catch (caught) {
        const kind = coverLeadError(caught);
        if (kind !== 'down') {
          setError(kind);
          setIsBusy(false);
          return;
        }
        // The API is down: the email waits here and the file starts.
        writePendingEmail(browserCoverStorage(), email);
        setSentTo(null);
      }
      const isSaved = await saveFile();
      setIsBusy(false);
      setDialog(isSaved ? 'done' : 'closed');
    },
    [saveFile, sendLead]
  );

  const close = useCallback(() => setDialog('closed'), []);
  const clearError = useCallback(() => setError(null), []);
  return { start, submit, close, clearError, dialog, error, isBusy, isFileFailed, sentTo };
}
